import { Injectable } from '@angular/core';
import { argbFromHex, hexFromArgb, Hct, TonalPalette } from '@material/material-color-utilities';

/**
 * Runtime per-client brand theming (SAAS).
 *
 * Material (Phase 3) reads every colour from `--mat-sys-*` system variables (theme-utils.scss sets
 * `use-system-variables: true`), and all custom CSS (Phase 2) reads `--app-primary-*`. Both are
 * emitted once by the compiled stylesheet for the DEFAULT brand. This service generates an exact
 * M3 tonal palette from an arbitrary client hex (material-color-utilities — the same engine Angular
 * Material's generator uses) and overrides the BRAND-bearing tokens in a single injected <style>,
 * re-theming the whole app without re-emitting any component CSS.
 *
 * Scope by design: only the PRIMARY + SECONDARY families, surface-tint and the `--app-primary-*`
 * ramp are overridden. Neutrals, surfaces, tertiary and error are left at their compiled defaults —
 * structure stays neutral and error stays red, so the client brand colours emphasis only (matches
 * the app's "green = emphasis, structure = neutral" hierarchy).
 */
@Injectable({ providedIn: 'root' })
export class BrandThemeService {
  /** RMBF BBC Chapter's brand navy (centuryNavy). The compiled stylesheet already emits the
   *  PINNED palette for this (theme-colors tone-40 is pinned to it), so we no-op rather than
   *  drift it via MCU. */
  static readonly DEFAULT_BRAND = '#1c2f57';

  /** Ready-made brand seeds for future per-client / SAAS themes. Pass any of these as the
   *  `brandColor` in an environment file and `applyBrandColor()` generates the full M3 palette. */
  static readonly BRAND_SEEDS: Readonly<Record<string, string>> = {
    blue: '#2196f3',
    gold: '#d18f00',
    copper: '#b87333',
    navyBlue: '#264f9d',
    centuryNavy: '#1c2f57',
    roseGold: '#e58c73'
  };

  private static readonly STYLE_ID = 'brand-theme-vars';

  /**
   * Apply a client brand colour at runtime. Safe to call with the default brand (no-ops) or an
   * invalid value (ignored). Idempotent — replaces any previously injected brand style.
   */
  applyBrandColor(hex: string | null | undefined): void {
    const seed = this.normalizeHex(hex);
    if (!seed) {
      return;
    }
    if (seed === BrandThemeService.DEFAULT_BRAND) {
      // Default brand: the compiled (pinned) palette is authoritative — remove any prior override.
      this.clear();
      return;
    }

    // Reproduces M3's CorePalette (the same formula themeFromSourceColor uses) without pulling in
    // its scoring/quantizer/blend code: primary = source hue at chroma≥48, secondary = hue at 16.
    const hct = Hct.fromInt(argbFromHex(seed));
    const primary = TonalPalette.fromHueAndChroma(hct.hue, Math.max(hct.chroma, 48));
    const secondary = TonalPalette.fromHueAndChroma(hct.hue, 16);
    this.inject(this.buildCss(seed, primary, secondary));
  }

  /** Remove the injected brand style, reverting to the compiled default palette. */
  clear(): void {
    document.getElementById(BrandThemeService.STYLE_ID)?.remove();
  }

  // ── palette → CSS ──────────────────────────────────────────────────────────
  private buildCss(seed: string, p: TonalPalette, s: TonalPalette): string {
    const t = (pal: TonalPalette, tone: number) => hexFromArgb(pal.tone(tone));
    const onSeed = this.contrastColor(seed); // white/black for legible text on the exact brand hex

    // Custom-CSS bridge ramp (--app-primary-*): mirrors theme-css-vars' tone mapping, with 500
    // pinned to the exact seed. Not darkened in dark mode (brand areas keep their colour), so :root
    // only. Neutrals are intentionally NOT overridden (structure stays neutral grey).
    const appRamp = [
      `--app-primary-100:${t(p, 95)}`,
      `--app-primary-200:${t(p, 90)}`,
      `--app-primary-300:${t(p, 80)}`,
      `--app-primary-400:${t(p, 60)}`,
      `--app-primary-500:${seed}`,
      `--app-primary-600:${t(p, 30)}`,
      `--app-primary-700:${t(p, 20)}`,
      `--app-primary-800:${t(p, 10)}`,
      `--app-primary-900:${t(p, 0)}`,
      `--app-primary:${seed}`,
      `--app-primary-contrast:${onSeed}`,
      `--app-primary-contrast-80:${t(p, 0)}`
    ].join(';');

    // Tone-based "fixed" brand roles are identical in light and dark.
    const fixed = [
      `--mat-sys-primary-fixed:${t(p, 90)}`,
      `--mat-sys-primary-fixed-dim:${t(p, 80)}`,
      `--mat-sys-on-primary-fixed:${t(p, 10)}`,
      `--mat-sys-on-primary-fixed-variant:${t(p, 30)}`,
      `--mat-sys-secondary-fixed:${t(s, 90)}`,
      `--mat-sys-secondary-fixed-dim:${t(s, 80)}`,
      `--mat-sys-on-secondary-fixed:${t(s, 10)}`,
      `--mat-sys-on-secondary-fixed-variant:${t(s, 30)}`
    ].join(';');

    // Light scheme brand roles (M3 light tone mapping; primary pinned to the exact seed).
    const light = [
      `--mat-sys-primary:${seed}`,
      `--mat-sys-on-primary:${onSeed}`,
      `--mat-sys-primary-container:${t(p, 90)}`,
      `--mat-sys-on-primary-container:${t(p, 30)}`,
      `--mat-sys-inverse-primary:${t(p, 80)}`,
      `--mat-sys-surface-tint:${seed}`,
      `--mat-sys-secondary:${t(s, 40)}`,
      `--mat-sys-on-secondary:${t(s, 100)}`,
      `--mat-sys-secondary-container:${t(s, 90)}`,
      `--mat-sys-on-secondary-container:${t(s, 30)}`,
      fixed,
      appRamp
    ].join(';');

    // Dark scheme brand roles (M3 dark tone mapping).
    const dark = [
      `--mat-sys-primary:${t(p, 80)}`,
      `--mat-sys-on-primary:${t(p, 20)}`,
      `--mat-sys-primary-container:${t(p, 30)}`,
      `--mat-sys-on-primary-container:${t(p, 90)}`,
      `--mat-sys-inverse-primary:${t(p, 40)}`,
      `--mat-sys-surface-tint:${t(p, 80)}`,
      `--mat-sys-secondary:${t(s, 80)}`,
      `--mat-sys-on-secondary:${t(s, 20)}`,
      `--mat-sys-secondary-container:${t(s, 30)}`,
      `--mat-sys-on-secondary-container:${t(s, 90)}`,
      fixed
    ].join(';');

    return `:root{${light}}.dark-mode{${dark}}`;
  }

  // ── DOM ────────────────────────────────────────────────────────────────────
  private inject(css: string): void {
    let el = document.getElementById(BrandThemeService.STYLE_ID) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = BrandThemeService.STYLE_ID;
      // Appended last in <head> so it wins over the bundled stylesheet at equal specificity.
      document.head.appendChild(el);
    }
    el.textContent = css;
  }

  // ── colour helpers ─────────────────────────────────────────────────────────
  /** Accept #rgb / #rrggbb (with or without leading #); returns lowercase #rrggbb or null. */
  private normalizeHex(hex: string | null | undefined): string | null {
    if (!hex) {
      return null;
    }
    let h = hex.trim().replace(/^#/, '').toLowerCase();
    if (/^[0-9a-f]{3}$/.test(h)) {
      h = h
        .split('')
        .map(c => c + c)
        .join('');
    }
    return /^[0-9a-f]{6}$/.test(h) ? `#${h}` : null;
  }

  /** White or black, whichever is more legible on the given colour (WCAG relative luminance). */
  private contrastColor(hex: string): string {
    const n = parseInt(hex.slice(1), 16);
    const lin = (c: number) => {
      const v = c / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    const l = 0.2126 * lin((n >> 16) & 0xff) + 0.7152 * lin((n >> 8) & 0xff) + 0.0722 * lin(n & 0xff);
    return l > 0.4 ? '#000000' : '#ffffff';
  }
}
